import { useState } from 'react'
import { X } from 'lucide-react'

interface HashtagInputProps {
  hashtags: string[]
  onChange: (hashtags: string[]) => void
  maxHashtags?: number
  onFocusCapture?: () => void
}

/**
 * Hashtag input with add/remove/keyboard handling.
 *
 * Designed for desktop editors (InstagramPostEditor, LinkedInPostEditor, TikTokVideoEditor).
 * MobileSectionEditor uses a buffered local-state pattern where edits are committed on
 * explicit "Save" — wire onChange to local state there, not the store directly.
 */
export function HashtagInput({
  hashtags,
  onChange,
  maxHashtags,
  onFocusCapture,
}: HashtagInputProps) {
  const [input, setInput] = useState('')
  const atLimit = maxHashtags != null && hashtags.length >= maxHashtags

  const addHashtag = (tag: string) => {
    if (atLimit) return
    const cleaned = tag.replace(/^#/, '').trim()
    if (cleaned && !hashtags.includes(cleaned)) {
      onChange([...hashtags, cleaned])
    }
  }

  const removeHashtag = (tag: string) => {
    onChange(hashtags.filter(h => h !== tag))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addHashtag(input)
      setInput('')
    }
    if (e.key === 'Backspace' && !input && hashtags.length > 0) {
      removeHashtag(hashtags[hashtags.length - 1])
    }
  }

  return (
    <div
      onFocusCapture={onFocusCapture}
      className="rounded-[10px] border border-line-bright bg-bg-3 p-2"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {hashtags.map(tag => (
          <span
            key={tag}
            className="flex items-center gap-1 rounded-full border border-line-bright bg-bg-4 py-1 pr-1 pl-2.5 text-[12px] font-medium text-paper"
          >
            #{tag}
            <button
              onClick={() => removeHashtag(tag)}
              aria-label={`Remove #${tag}`}
              className="flex h-4 w-4 items-center justify-center rounded-full text-paper-faint transition-colors hover:bg-bg-3 hover:text-paper"
            >
              <X size={12} />
            </button>
          </span>
        ))}
        {!atLimit && (
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={hashtags.length === 0 ? 'Add hashtags...' : ''}
            className="min-w-[80px] flex-1 bg-transparent py-0.5 text-[11px] text-paper outline-none placeholder:text-paper-faint"
          />
        )}
      </div>
    </div>
  )
}
